import React, {useCallback, useEffect, useState, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {
	Table,
	// Tooltip,
} from '@phonectas/unifon-ui-kit';
import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';
import UsersTableRow from './UsersTableRow';
import {LIMIT_PAYLOAD} from 'common/constants';
import TableDialog from '../TableDialog';
import UserDialogContent from './dialogContent/UserDialogContent';
import FilterOnIcon from 'common/components/icons/FilterOnIcon';
import FilterOffIcon from 'common/components/icons/FilterOffIcon';
import {StyledIconButton} from 'common/components/styled';
// import {useSmallScreen} from 'common/hooks/useScreenSize';
import {get} from 'lodash';
import {StyledSectionDiv} from '../../styled';
import {CustomerUser} from 'common/types/store/threeSixtyTypes';
// import SecretNumberIcon from 'common/components/icons/SecretNumberIcon';

interface UsersTableProps {
	id: string;
	userTableFilter?: boolean;
	setUserTableFilter?: (value: boolean) => void;
}

interface GetUsersParams {
    id: string;
    limit?: number;
    sort?: string;
    search?: string;
    statuses?: boolean;
};

const UsersTable: React.FC<UsersTableProps> = ({
	id, userTableFilter, setUserTableFilter
}) => {
	const [t] = useTranslation();
	const {
		getUsers,
		setActiveRow,
		getUpsellUser,
	} = useThreeSixtyNewStore((state) => ({
		getUsers: state.getUsers,
		setActiveRow: state.setActiveRow,
		getUpsellUser: state.getUpsellUser,
	}), shallow);
	const activeRow = useThreeSixtyNewStore((state) => state.activeRow);
	const [showDialog, setShowDialog] = useState(false);
	// const isSmallScreen = useSmallScreen('(max-width:1110px)');
	const {users, totalCount, accountNotFound, bnRedirect} = useThreeSixtyNewStore((state) => ({
		users: state.accountTables?.users,
		totalCount: state.accountTables?.users?.total_count,
		accountNotFound: state.accountNotFound,
		bnRedirect: state.bnRedirect?.user,
	}), shallow);
	const usersData = useMemo(() => users?.customer_users || [], [users]);
	const [bnUser, setBnUsers] = useState(usersData);
	const bnUserId = get(bnRedirect, 'id', '');

	useEffect(() => {
		if (bnRedirect && bnRedirect?.account_id === id) {
			const userExists = usersData.some((user) => user.id === bnRedirect.id);
			if (!userExists) {
				const updatedUsers = [bnRedirect, ...usersData];
				setBnUsers(updatedUsers);
			} else {
				const updatedUsers = [bnRedirect, ...usersData.filter((user) => user.id !== bnRedirect.id)];
				setBnUsers(updatedUsers);
			}
		} else {
			setBnUsers(usersData);
		}
	}, [bnRedirect, usersData, id]);

	const handleFilterOn = useCallback(() => {
		if (setUserTableFilter) setUserTableFilter(true);
		// fetchUsers(LIMIT_PAYLOAD, id);
		getUsers({
			id: id,
			limit: LIMIT_PAYLOAD,
		});
	}, [id, getUsers, setUserTableFilter]);
	const handleFilterOff = useCallback(() => {
		if (setUserTableFilter) setUserTableFilter(false);
		getUsers({
			id: id,
			limit: LIMIT_PAYLOAD,
			statuses: false,
		});
	}, [id, getUsers, setUserTableFilter]);
	const onHideDialog = useCallback(() => {
		setShowDialog(false);
	}, []);
	const handleRowClick = useCallback((item: CustomerUser) => {
		setActiveRow(item);
		if (item?.id) {
			const idToInt = parseInt(item.id, 10);
			getUpsellUser(idToInt);
		}
		setShowDialog(true);
	}, [setActiveRow, getUpsellUser]);
	const onLoadMore = useCallback((size: number) => {
		if (totalCount && totalCount <= size) return;

		const params: GetUsersParams = {
			id: id,
			limit: size + LIMIT_PAYLOAD,
			sort: '',
			// Keep the table search, the response echoes the search it was made with
			search: users?.filters?.search_text ?? '',
			...(typeof userTableFilter === 'boolean' ? {statuses: userTableFilter} : {}),
		};
		getUsers(params);
		setBnUsers(usersData);
	}, [id, totalCount, userTableFilter, usersData, getUsers, users?.filters?.search_text]);

	const renderItem = useCallback((item: CustomerUser, index: number) => (
		<UsersTableRow
			key={index}
			data={item}
			onClick={handleRowClick}
			bnUser={bnUserId}
		/>
	), [handleRowClick, bnUserId]);

	useEffect(() => {
		if (accountNotFound) return;
		if (totalCount === 0 && users?.customer_users === null && users?.filters?.account_id === id) return;
		const isAccountIdMatched = usersData !== null && id === usersData[0]?.account_id;
		const isUsersFetched = users !== null && isAccountIdMatched;
		const isUsersFilterMatched = users?.filters?.account_id === id && usersData === null;

		if (isUsersFetched || isUsersFilterMatched) return;

		getUsers({
			id: id,
			limit: LIMIT_PAYLOAD,
			sort: '',
			search: '',
			statuses: true,
		});
	}, [getUsers, id, usersData, accountNotFound, users, totalCount]);

	return (
		<StyledSectionDiv
			style={{
				display: 'flex',
				flexDirection: 'column',
				flexGrow: '1',
				overflow: 'hidden',
				padding: '16px 8px'
			}}
			$endDiv
			$fullHeight
		>
			<Table
				items={bnUser || []}
				renderItem={(item, index) => renderItem(item, index)}
				virtualized
				rowHeight={75}
				onScrollDown={onLoadMore}
			>
				<Table.Row style={{padding: '0px'}}>
					{/* for when hidden number is ready */}
					{/* <Table.Cell width={14}>
						<Tooltip text={t('Hidden Number')}>
							<div style={{width: '14px', justifyContent: 'center', display: 'flex'}}>
								<SecretNumberIcon />
							</div>
						</Tooltip>
					</Table.Cell> */}
					<Table.Cell width={140}>{t('Name')}</Table.Cell>
					<Table.Cell width={90}>{t('Number')}</Table.Cell>
					<Table.Cell
						width={42}
					>
						{t('Email')}
					</Table.Cell>
					<Table.Cell style={{fontSize: '12px'}}>
						<span style={{alignItems: 'center', display: 'flex'}}>
							{t('Status')}
							{
								userTableFilter ? (
									<StyledIconButton
										onClick={handleFilterOff}
										$width={24}
										$height={22}
										style={{marginLeft: '5px'}}
									>
										<FilterOnIcon />
									</StyledIconButton>
								) : (
									<StyledIconButton
										onClick={handleFilterOn}
										$width={24}
										$height={22}
										style={{marginLeft: '5px'}}
									>
										<FilterOffIcon />
									</StyledIconButton>
								)
							}
						</span>
					</Table.Cell>
					<Table.Cell>{t('SIM type')}</Table.Cell>
					<Table.Cell width={70}>{t('Used')}</Table.Cell>
					<Table.Cell>{t('Product')}</Table.Cell>
					<Table.Cell>{t('PIN / PUK')}</Table.Cell>
					<Table.Cell width={40} >{t('NPS')}</Table.Cell>
				</Table.Row>
			</Table>
			<TableDialog
				show={showDialog}
				width={950}
				onClose={onHideDialog}
			>
				{activeRow &&
						<UserDialogContent
							data={activeRow}
							onClose={onHideDialog}
						/>
				}
			</TableDialog>
		</StyledSectionDiv>
	);
}

export default React.memo(UsersTable);
